import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useAppStateContext } from 'src/AppState/AppState';
import { PlatformName, LegendWrapper, PlatformDot } from 'src/main/AdminPage/AppUpdate/AppUpdate.style';
import { MobileUpdateVersionModelType } from 'src/api/config/mobileAppUpdateVersion/getAppUpdateVersion';
import { AppUpdateVersionState, platformOptions } from 'src/AppState/CmsState/AppUpdateVersion/AppUpdateVersion';
import { Select } from 'src/domains/layouts/shared/formComponents/Select/components/Select';
import { Button } from 'src/domains/layouts/shared/buttons/Button';
import { ActionsWrapper, LabelWrapper, ListContainer, ListItem, ToggleWrapper } from 'src/domains/layouts/shared/cms/list/List.style';
import { InputError } from 'src/domains/layouts/shared/formComponents/FormComponents.style';
import { Input } from 'src/domains/layouts/shared/formComponents/input/Input';
import { ColumnOneOfTwo, TwoColumnResponsiveContentReverse } from 'src/domains/layouts/shared/layout/Layout.style';
import { FormHeader, PageHeader } from '../AdminPage.styles';
import { MoreActions } from 'src/domains/layouts/shared/moreActions/MoreActions';

class State {
    constructor(
        private readonly appUpdateVersionState: AppUpdateVersionState,
        private readonly appVersion: MobileUpdateVersionModelType,
    ) { }

    setActiveFlag = async (): Promise<void> => {
        this.appUpdateVersionState.setActiveFlag(this.appVersion.app_version, this.appVersion.platform);
    }

    handleRemoveApiCall = async (): Promise<void> => {
        await this.appUpdateVersionState.handleRemoveApiCall(this.appVersion.app_version, this.appVersion.platform);
    }
}

interface PropsTypes {
    appUpdateVersionState: AppUpdateVersionState;
    appVersion: MobileUpdateVersionModelType;
}

const AppMobileVersionItem = observer(({ appUpdateVersionState, appVersion }: PropsTypes) => {
    const [state] = useState(() => new State(appUpdateVersionState, appVersion));
    const platform = platformOptions.find((option) => option.key === appVersion.platform);

    return (
        <ListItem direction='row'>
            <LabelWrapper>
                <PlatformDot platform={platform?.key ?? ''}/>
                {appVersion.app_version}
            </LabelWrapper>

            <ActionsWrapper>
                <ToggleWrapper label='Forced update' toggleActive={state.setActiveFlag} isActive={appVersion.force_update} isDisabled={false} />
                <MoreActions onDelete={state.handleRemoveApiCall} />
            </ActionsWrapper>
        </ListItem>
    );
});


export const AppUpdate = observer(() => {
    const { cmsState } = useAppStateContext();
    const { appUpdateVersionState } = cmsState;

    const {
        appMobileVersionSortedList,
        appVersion, platform, errorMessageForList,
        handleCreate, isLoading
    } = appUpdateVersionState;

    return (
        <TwoColumnResponsiveContentReverse>
            <ColumnOneOfTwo>
                <PageHeader>Force app update</PageHeader>

                <LegendWrapper >
                    <PlatformDot platform='android' isLegend={true} />
                    <PlatformName platform='android'>Android</PlatformName>
                    <PlatformDot platform='ios' isLegend={true}/>
                    <PlatformName platform='ios'>IOS</PlatformName>
                </LegendWrapper>

                <ListContainer areLoading={isLoading}>
                    <>
                        {appMobileVersionSortedList.map(version => {
                            return (
                                <AppMobileVersionItem
                                    key={`${version.app_version}-${version.platform}`}
                                    appUpdateVersionState={appUpdateVersionState}
                                    appVersion={version}
                                />
                            );
                        })}

                    </>
                </ListContainer>
            </ColumnOneOfTwo>

            <ColumnOneOfTwo>
                <FormHeader>Add app version</FormHeader>
                <Input
                    label='App version'
                    formInputState={appVersion}
                />
                <Select label='Platform' selectOptions={platformOptions} stateValue={platform}/>

                <Button onClick={handleCreate}>Add</Button>
                {errorMessageForList === null ? null : <InputError>{errorMessageForList}</InputError>}
            </ColumnOneOfTwo>
        </TwoColumnResponsiveContentReverse>
    );
});
